import { Component, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CreateRequirementBandDto, CreateRequirementDto, CreateRequirementLimitDto, CreateRequirementAgeRangeDto } from '../../../../core/data/new-dtos/insurance-type.dtos';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'app-requirement-band-selector',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective],
  templateUrl: './requirement-band-selector.component.html',
  styleUrl: './requirement-band-selector.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RequirementBandSelectorComponent),
      multi: true
    },
    provideNgxMask()
  ]
})
export class RequirementBandSelectorComponent implements ControlValueAccessor {
  form: FormGroup;

  get bands(): FormArray {
    return this.form.get('bands') as FormArray;
  }

  currencies = ['DOP', 'USD'];

  private isWriteValue = false;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      bands: this.fb.array([])
    });

    this.form.valueChanges.subscribe(val => {
      if (!this.isWriteValue) {
        this.onChange(val.bands);
        this.onTouched();
      }
    });
  }

  getLimits(bandIndex: number): FormArray {
    return this.bands.at(bandIndex).get('limits') as FormArray;
  }

  getAgeRanges(bandIndex: number): FormArray {
    return this.bands.at(bandIndex).get('ageRanges') as FormArray;
  }

  getRequirements(bandIndex: number): FormArray {
    return this.bands.at(bandIndex).get('requirements') as FormArray;
  }

  createBandGroup(band?: CreateRequirementBandDto): FormGroup {
    return this.fb.group({
      limits: this.fb.array(
        band?.limits?.map(l => this.createLimitGroup(l)) || []
      ),
      ageRanges: this.fb.array(
        band?.ageRanges?.map(ar => this.createAgeRangeGroup(ar)) || []
      ),
      requirements: this.fb.array(
        band?.requirements?.map(r => this.createRequirementGroup(r)) || []
      )
    });
  }

  createLimitGroup(limit?: CreateRequirementLimitDto): FormGroup {
    return this.fb.group({
      currency: [limit?.currency || 'DOP', Validators.required],
      amountFrom: [limit?.amountFrom || 0, [Validators.required, Validators.min(0)]],
      amountTo: [limit?.amountTo || null]
    });
  }

  createAgeRangeGroup(ageRange?: CreateRequirementAgeRangeDto): FormGroup {
    return this.fb.group({
      ageFrom: [ageRange?.ageFrom || null, [Validators.required, Validators.min(0)]],
      ageTo: [ageRange?.ageTo || null, [Validators.required, Validators.min(0)]]
    });
  }

  createRequirementGroup(req?: CreateRequirementDto): FormGroup {
    return this.fb.group({
      name: [req?.name || '', Validators.required]
    });
  }

  addBand() {
    this.bands.push(this.createBandGroup());
  }

  removeBand(index: number) {
    this.bands.removeAt(index);
  }

  addLimit(bandIndex: number) {
    this.getLimits(bandIndex).push(this.createLimitGroup());
  }

  removeLimit(bandIndex: number, limitIndex: number) {
    this.getLimits(bandIndex).removeAt(limitIndex);
  }

  addAgeRange(bandIndex: number) {
    this.getAgeRanges(bandIndex).push(this.createAgeRangeGroup());
  }

  removeAgeRange(bandIndex: number, rangeIndex: number) {
    this.getAgeRanges(bandIndex).removeAt(rangeIndex);
  }

  addRequirement(bandIndex: number) {
    this.getRequirements(bandIndex).push(this.createRequirementGroup());
  }

  removeRequirement(bandIndex: number, reqIndex: number) {
    this.getRequirements(bandIndex).removeAt(reqIndex);
  }

  writeValue(obj: any): void {
    if (obj) {
      this.isWriteValue = true;
      this.bands.clear();
      (obj as CreateRequirementBandDto[]).forEach(band => {
        this.bands.push(this.createBandGroup(band));
      });
      this.isWriteValue = false;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  private onChange: any = () => { };
  private onTouched: any = () => { };
}
